import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LegalAppendiceDTO } from "../../models/LegalAppendice.model";
import {
  createLegalAppendice,
  getLegalAppendices,
  deleteLegalAppendice,
} from "../thunks/legalAppendiceThunks";

interface LegalAppendiceState {
  legalAppendices: LegalAppendiceDTO[];
  selectedAppendice: LegalAppendiceDTO | null;
  loading: boolean;
  createLoading: boolean;
  deleteLoading: boolean;
  error: string | null;
  createError: string | null;
  deleteError: string | null;
  createSuccess: boolean;
  deleteSuccess: boolean;
}

const initialState: LegalAppendiceState = {
  legalAppendices: [],
  selectedAppendice: null,
  loading: false,
  createLoading: false,
  deleteLoading: false,
  error: null,
  createError: null,
  deleteError: null,
  createSuccess: false,
  deleteSuccess: false,
};

const legalAppendiceSlice = createSlice({
  name: "legalAppendice",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.deleteError = null;
    },
    clearSuccess: (state) => {
      state.createSuccess = false;
      state.deleteSuccess = false;
    },
    selectAppendice: (state, action: PayloadAction<number>) => {
      state.selectedAppendice =
        state.legalAppendices.find(
          (appendice) => appendice.legalAppendice_ID === action.payload,
        ) || null;
    },
    clearSelectedAppendice: (state) => {
      state.selectedAppendice = null;
    },
  },
  extraReducers: (builder) => {
    // Get Legal Appendices
    builder
      .addCase(getLegalAppendices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLegalAppendices.fulfilled, (state, action) => {
        state.loading = false;
        state.legalAppendices = action.payload.legalAppendices;
      })
      .addCase(getLegalAppendices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Legal Appendice
    builder
      .addCase(createLegalAppendice.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createLegalAppendice.fulfilled, (state, _action) => {
        state.createLoading = false;
        state.createSuccess = true;
      })
      .addCase(createLegalAppendice.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload as string;
      });

    // Delete Legal Appendice
    builder
      .addCase(deleteLegalAppendice.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteLegalAppendice.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        // Remove from array
        state.legalAppendices = state.legalAppendices.filter(
          (appendice) =>
            appendice.legalAppendice_ID !== action.meta.arg.legalAppendiceID,
        );
      })
      .addCase(deleteLegalAppendice.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      });
  },
});

export const {
  clearErrors,
  clearSuccess,
  selectAppendice,
  clearSelectedAppendice,
} = legalAppendiceSlice.actions;

export default legalAppendiceSlice.reducer;
